import { makeCall, type FunctionsClient } from './api';

/**
 * The door to the subscription data: users, revenue, finance, emails. Every action goes through
 * the `admin-billing` Edge Function, which checks the `admins` table itself
 * (supabase/functions/admin-billing/index.ts lists the actions).
 */

export type UserStatus = 'trial' | 'active' | 'past_due' | 'cancelled' | 'locked' | 'grant';

/** access_state(user) as the server answers it. */
export interface AccessInfo {
  enforced: boolean;
  state: 'active' | 'grant' | 'trial' | 'locked';
  reason: string;
  until: string | null;
  trial_ends_at: string | null;
  locked_since: string | null;
  subscription: {
    status: string;
    plan: string;
    current_period_end: string | null;
    cancel_at: string | null;
  } | null;
}

export interface UserRow {
  user_id: string;
  email: string | null;
  created_at: string;
  last_sign_in_at: string | null;
  trial_ends_at: string | null;
  status: UserStatus;
  access: AccessInfo;
  plan: string | null;
  last_activity_at: string | null;
}

export type UserSort = 'created_at' | 'last_sign_in_at' | 'trial_ends_at' | 'email';

export interface UsersQuery {
  search?: string;
  state?: UserStatus | null;
  sort?: UserSort;
  direction?: 'asc' | 'desc';
  page?: number;
  pageSize?: number;
}

export interface TimelineEvent {
  at: string;
  kind:
    'signup' | 'trial' | 'subscription' | 'payment' | 'refund' | 'chargeback' | 'grant' | 'email';
  title: string;
  detail?: string;
}

export interface AccessGrant {
  id: string;
  reason: string;
  starts_at: string;
  ends_at: string | null;
  revoked_at: string | null;
  created_at: string;
  granted_by_email: string | null;
}

export interface UserDetail {
  user: {
    user_id: string;
    email: string | null;
    created_at: string;
    last_sign_in_at: string | null;
  };
  billing: {
    trial_ends_at: string;
    access_ended_at: string | null;
    paddle_customer_id: string | null;
    reminder_emails_opt_out_at: string | null;
  } | null;
  access: AccessInfo;
  status: UserStatus;
  last_activity_at: string | null;
  grants: AccessGrant[];
  timeline: TimelineEvent[];
}

export interface MonthPoint {
  /** 'YYYY-MM' */
  month: string;
  new_subscriptions: number;
  cancellations: number;
}

export interface Revenue {
  active_subscribers: number;
  subscribed_in_trial: number;
  trials_running: number;
  trials_ended: number;
  trials_converted: number;
  /** Per currency, net of VAT, in cents. */
  mrr: Array<{ currency: string; amount: number; subscriptions: number }>;
  months: MonthPoint[];
}

export type FinanceGroup = 'month' | 'country';

export interface FinanceRow {
  /** 'YYYY-MM', a country code, or null (country unknown); null in the totals. */
  grp: string | null;
  currency: string;
  payout_currency: string;
  payments: number;
  gross: number;
  vat: number;
  fees: number;
  refunds: number;
  chargebacks: number;
  refund_count: number;
  chargeback_count: number;
  net: number;
}

export interface FinanceQuery {
  /** Inclusive days, 'YYYY-MM-DD'. */
  from: string;
  to: string;
  group: FinanceGroup;
}

export interface FinanceReport {
  rows: FinanceRow[];
  totals: FinanceRow[];
}

export interface EmailTemplate {
  key: string;
  kind: 'service' | 'reminder';
  description: string;
  subject: string;
  body: string;
  /** The {{names}} the subject and text use. */
  variables: string[];
  /** Example values for the preview. */
  sample: Record<string, string>;
  updated_at: string;
}

export type EmailAnchor = 'signup' | 'trial_end' | 'access_ended';

export interface EmailRule {
  id: string;
  template_key: string;
  anchor: EmailAnchor;
  offset_days: number;
  only_unsubscribed: boolean;
  active: boolean;
}

export type EmailStatus =
  'queued' | 'sent' | 'delivered' | 'delayed' | 'bounced' | 'complained' | 'failed';

export interface EmailLogRow {
  id: string;
  user_id: string | null;
  template_key: string;
  rule_id: string | null;
  to_email: string;
  status: EmailStatus;
  error: string | null;
  created_at: string;
  updated_at: string;
}

export interface EmailLogQuery {
  userId?: string | null;
  templateKey?: string | null;
  status?: EmailStatus | null;
  search?: string;
  page?: number;
  pageSize?: number;
}

export type RuleFields = Pick<EmailRule, 'template_key' | 'anchor' | 'offset_days'> &
  Partial<Pick<EmailRule, 'active' | 'only_unsubscribed'>>;

export interface BillingApi {
  listUsers(query?: UsersQuery): Promise<{ users: UserRow[]; total: number }>;
  getUser(userId: string): Promise<UserDetail>;
  /** Returns the new trial end. */
  extendTrial(userId: string, days: number): Promise<string>;
  /** `endsAt` null = free access without an end. */
  grant(userId: string, endsAt: string | null, reason: string): Promise<void>;
  revokeGrant(userId: string): Promise<void>;
  revenue(): Promise<Revenue>;
  financeReport(query: FinanceQuery): Promise<FinanceReport>;
  financeCsv(query: FinanceQuery): Promise<{ csv: string; filename: string }>;
  listTemplates(): Promise<EmailTemplate[]>;
  updateTemplate(
    key: string,
    fields: Partial<Pick<EmailTemplate, 'subject' | 'body' | 'description'>>
  ): Promise<EmailTemplate>;
  listRules(): Promise<EmailRule[]>;
  createRule(fields: RuleFields): Promise<EmailRule>;
  updateRule(id: string, fields: Partial<RuleFields>): Promise<EmailRule>;
  deleteRule(id: string): Promise<void>;
  /** Queues the template to the admin's own address; returns that address. */
  testSend(templateKey: string): Promise<string>;
  listEmails(query?: EmailLogQuery): Promise<{ emails: EmailLogRow[]; total: number }>;
  setSiteHidden(wordId: string, hidden: boolean): Promise<void>;
  /** Which of these library words are hidden from the public website. */
  hiddenWords(wordIds: string[]): Promise<string[]>;
}

export function createBillingApi(client: FunctionsClient): BillingApi {
  const call = makeCall(client, 'admin-billing');

  return {
    async listUsers(query = {}) {
      const r = await call<{ users?: UserRow[]; total?: number }>('users.list', {
        search: query.search ?? '',
        ...(query.state ? { state: query.state } : {}),
        sort: query.sort ?? 'created_at',
        direction: query.direction ?? 'desc',
        page: query.page ?? 1,
        page_size: query.pageSize ?? 50,
      });
      return { users: r.users ?? [], total: r.total ?? 0 };
    },
    async getUser(userId) {
      const r = await call<UserDetail>('users.get', { user_id: userId });
      return { ...r, grants: r.grants ?? [], timeline: r.timeline ?? [] };
    },
    async extendTrial(userId, days) {
      const r = await call<{ trial_ends_at: string }>('users.extend_trial', {
        user_id: userId,
        days,
      });
      return r.trial_ends_at;
    },
    async grant(userId, endsAt, reason) {
      await call('users.grant', { user_id: userId, ends_at: endsAt, reason });
    },
    async revokeGrant(userId) {
      await call('users.revoke_grant', { user_id: userId });
    },
    async revenue() {
      const r = await call<Revenue>('revenue.overview');
      return { ...r, mrr: r.mrr ?? [], months: r.months ?? [] };
    },
    async financeReport(query) {
      const r = await call<Partial<FinanceReport>>('finance.report', { ...query });
      return { rows: r.rows ?? [], totals: r.totals ?? [] };
    },
    async financeCsv(query) {
      return call<{ csv: string; filename: string }>('finance.csv', { ...query });
    },
    async listTemplates() {
      const r = await call<{ templates?: EmailTemplate[] }>('emails.templates.list');
      return r.templates ?? [];
    },
    async updateTemplate(key, fields) {
      const r = await call<{ template: EmailTemplate }>('emails.templates.update', {
        key,
        ...fields,
      });
      return r.template;
    },
    async listRules() {
      const r = await call<{ rules?: EmailRule[] }>('emails.rules.list');
      return r.rules ?? [];
    },
    async createRule(fields) {
      const r = await call<{ rule: EmailRule }>('emails.rules.create', { ...fields });
      return r.rule;
    },
    async updateRule(id, fields) {
      const r = await call<{ rule: EmailRule }>('emails.rules.update', { id, ...fields });
      return r.rule;
    },
    async deleteRule(id) {
      await call('emails.rules.delete', { id });
    },
    async testSend(templateKey) {
      const r = await call<{ to: string }>('emails.test_send', { template_key: templateKey });
      return r.to;
    },
    async listEmails(query = {}) {
      const r = await call<{ emails?: EmailLogRow[]; total?: number }>('emails.log', {
        ...(query.userId ? { user_id: query.userId } : {}),
        ...(query.templateKey ? { template_key: query.templateKey } : {}),
        ...(query.status ? { status: query.status } : {}),
        search: query.search ?? '',
        page: query.page ?? 1,
        page_size: query.pageSize ?? 50,
      });
      return { emails: r.emails ?? [], total: r.total ?? 0 };
    },
    async setSiteHidden(wordId, hidden) {
      await call('words.set_site_hidden', { word_id: wordId, hidden });
    },
    async hiddenWords(wordIds) {
      if (!wordIds.length) return [];
      const r = await call<{ hidden?: string[] }>('words.site_hidden', { word_ids: wordIds });
      return r.hidden ?? [];
    },
  };
}
